import React, { Component } from "react";
import axios from "axios";
import { Spin, Result, Form, Input, Select, Button, Upload, message, Tag } from "antd";
import { CameraOutlined, CheckCircleTwoTone, ToolOutlined, AlertOutlined } from "@ant-design/icons";
import DocumentoPublicoCMMS from "./DocumentoPublicoCMMS";

const { Option } = Select;

// Pagina PUBLICA que abre el QR de la etiqueta de un equipo (sin iniciar sesion).
// Muestra la ficha del equipo (sin costos) y permite al hospital reportar una falla,
// que llega al ERP como ticket "Recibido". Ruta: /activoCMMS/:token

const COLORES = { 1: "#2e9e6b", 2: "#d9962b", 3: "#d64545", 4: "#7a8794" };

const fecha = (f) => {
    if (!f) return "—";
    const d = new Date(f);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" });
};

const getBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
    });

// Reduce la foto del celular (a ~1280px) antes de mandarla.
const reducirImagen = (file) =>
    new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const escala = Math.min(1, 1280 / Math.max(img.width, img.height));
            const canvas = document.createElement("canvas");
            canvas.width = Math.round(img.width * escala);
            canvas.height = Math.round(img.height * escala);
            canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL("image/jpeg", 0.8));
            URL.revokeObjectURL(img.src);
        };
        img.onerror = () => getBase64(file).then(resolve);
        img.src = URL.createObjectURL(file);
    });

const estilos = {
    pagina: { minHeight: "100vh", background: "#eef2f5", padding: "16px 12px 40px", fontFamily: "'Segoe UI', Calibri, Arial, sans-serif" },
    contenedor: { maxWidth: 560, margin: "0 auto" },
    tarjeta: { background: "#fff", borderRadius: 12, boxShadow: "0 2px 10px rgba(31,42,55,.08)", padding: 16, marginBottom: 12 },
    etiqueta: { fontSize: 12, color: "#7a8794" },
    valor: { fontSize: 15, color: "#1f2a37", fontWeight: 500 },
};

class ActivoPublico extends Component {
    formRef = React.createRef();
    state = { cargando: true, ficha: null, error: null, reportando: false, enviando: false, folio: null, fotos: [] };

    get token() {
        return this.props.match?.params?.token;
    }

    // /activoCMMS/documento/<token>: documento abierto desde la app (ver DocumentoPublicoCMMS).
    get esDocumento() {
        return this.token === "documento";
    }

    componentDidMount() {
        if (this.esDocumento) return;
        this.consultar();
    }

    consultar = async () => {
        this.setState({ cargando: true });
        try {
            const res = await axios.post("cmmsPublico/fichaPublicaActivoCMMS", { token: this.token });
            if (res.data.success === 2) this.setState({ ficha: res.data.ficha });
            else this.setState({ error: res.data.message || "No se encontró el equipo" });
        } catch (error) {
            this.setState({ error: "No se pudo consultar el equipo. Revise su conexión." });
        } finally {
            this.setState({ cargando: false });
        }
    };

    enviar = async (values) => {
        this.setState({ enviando: true });
        try {
            const fotos = [];
            for (const f of this.state.fotos.slice(0, 3)) fotos.push({ name: f.name, url: await reducirImagen(f.originFileObj || f) });
            const res = await axios.post("cmmsPublico/reportarFallaActivoCMMS", { token: this.token, ...values, fotos });
            if (res.data.success === 2) this.setState({ folio: res.data.folio, reportando: false, fotos: [] });
            else message.error(res.data.message || "No se pudo enviar el reporte");
        } catch (error) {
            message.error("No se pudo enviar el reporte. Revise su conexión.");
        } finally {
            this.setState({ enviando: false });
        }
    };

    renderDato(etiqueta, valor) {
        return (
            <div style={{ marginBottom: 8 }}>
                <div style={estilos.etiqueta}>{etiqueta}</div>
                <div style={estilos.valor}>{valor || "—"}</div>
            </div>
        );
    }

    render() {
        if (this.esDocumento) return <DocumentoPublicoCMMS token={window.location.pathname.split("/")[3] || ""} />;
        const { cargando, ficha, error, folio } = this.state;
        if (cargando)
            return (
                <div style={{ ...estilos.pagina, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Spin size="large" />
                </div>
            );
        if (error || !ficha)
            return (
                <div style={estilos.pagina}>
                    <div style={estilos.contenedor}>
                        <Result status="warning" title="Equipo no encontrado" subTitle={error || "Este código QR ya no es válido."} />
                    </div>
                </div>
            );
        const a = ficha.activo;
        return (
            <div style={estilos.pagina}>
                <div style={estilos.contenedor}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                        {ficha.empresa.logo && <img src={ficha.empresa.logo} alt="" style={{ height: 40, maxWidth: 120, objectFit: "contain" }} />}
                        <div>
                            <div style={{ fontWeight: 600, color: "#1f2a37" }}>{ficha.empresa.nombre}</div>
                            <div style={{ fontSize: 12, color: "#7a8794" }}>Servicio de mantenimiento de equipo médico</div>
                        </div>
                    </div>

                    <div style={estilos.tarjeta}>
                        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
                            {a.foto ? (
                                <img src={a.foto} alt="" style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 10 }} />
                            ) : (
                                <div style={{ width: 72, height: 72, borderRadius: 10, background: "#e8f7fb", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <ToolOutlined style={{ fontSize: 28, color: "#2fb8d6" }} />
                                </div>
                            )}
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 19, fontWeight: 600, color: "#1f2a37", lineHeight: 1.2 }}>{a.tipo}</div>
                                <div style={{ color: "#5b6b7c" }}>{[a.marca, a.modelo].filter(Boolean).join(" · ")}</div>
                                <Tag color={COLORES[a.estatus]} style={{ marginTop: 6, color: "#fff", border: 0 }}>
                                    {a.estatusNombre}
                                </Tag>
                            </div>
                        </div>
                        {this.renderDato("No. de serie", a.numeroSerie)}
                        {a.numeroInventario && this.renderDato("No. de inventario", a.numeroInventario)}
                        {a.cliente && this.renderDato("Cliente", a.cliente)}
                        {a.ubicacion && this.renderDato("Ubicación", a.ubicacion)}
                        <div style={{ display: "flex", gap: 12 }}>
                            <div style={{ flex: 1 }}>{this.renderDato("Último mantenimiento", fecha(a.ultimoPreventivo))}</div>
                            <div style={{ flex: 1 }}>{this.renderDato("Próximo mantenimiento", fecha(a.proximoPreventivo))}</div>
                        </div>
                        <div style={{ fontSize: 11, color: "#9aa7b4" }}>Folio del equipo: {a.codigo}</div>
                    </div>

                    {(ficha.certificados || []).length > 0 && (
                        <div style={estilos.tarjeta}>
                            <div style={{ fontWeight: 600, marginBottom: 8 }}>Certificados de verificación</div>
                            {ficha.certificados.map((c, i) => (
                                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderTop: i ? "1px solid #f0f2f5" : 0, fontSize: 13 }}>
                                    <span>
                                        <b>{c.nombre}</b>
                                        <div style={{ fontSize: 11, color: "#7a8794" }}>
                                            {c.folio} · emitido {fecha(c.fecha)}
                                        </div>
                                    </span>
                                    {c.cumple === false ? (
                                        <span style={{ color: "#d64545", textAlign: "right", fontWeight: 600 }}>
                                            No cumple
                                            <div style={{ fontWeight: 400, fontSize: 11 }}>No apto para su uso</div>
                                        </span>
                                    ) : (
                                        <span style={{ color: "#2e9e6b", textAlign: "right" }}>
                                            Vigente hasta
                                            <div style={{ fontWeight: 600 }}>{fecha(c.vigenciaHasta)}</div>
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {ficha.servicios.length > 0 && (
                        <div style={estilos.tarjeta}>
                            <div style={{ fontWeight: 600, marginBottom: 8 }}>Últimos servicios</div>
                            {ficha.servicios.map((s, i) => (
                                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderTop: i ? "1px solid #f0f2f5" : 0, fontSize: 13 }}>
                                    <span>
                                        <b>{s.tipo}</b>
                                        <div style={{ fontSize: 11, color: "#7a8794" }}>{s.folio}</div>
                                    </span>
                                    <span style={{ color: "#5b6b7c" }}>{fecha(s.fecha)}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {folio ? (
                        <div style={estilos.tarjeta}>
                            <Result
                                icon={<CheckCircleTwoTone twoToneColor="#2e9e6b" />}
                                title="Reporte enviado"
                                subTitle={`Su folio es ${folio}. El equipo de servicio ya recibió el aviso y se pondrá en contacto.`}
                                extra={
                                    <Button onClick={() => this.setState({ folio: null })} type="default">
                                        Reportar otra falla
                                    </Button>
                                }
                            />
                        </div>
                    ) : !this.state.reportando ? (
                        <Button type="primary" danger block size="large" icon={<AlertOutlined />} onClick={() => this.setState({ reportando: true })} style={{ height: 52, borderRadius: 10, fontSize: 16 }}>
                            Reportar una falla
                        </Button>
                    ) : (
                        <div style={estilos.tarjeta}>
                            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 10 }}>Reportar una falla</div>
                            <Form ref={this.formRef} layout="vertical" onFinish={this.enviar} initialValues={{ tipoSolicitud: "Falla" }}>
                                <Form.Item label="¿Qué le pasa al equipo?" name="solicitud" rules={[{ required: true, message: "Describa la falla" }]}>
                                    <Input.TextArea rows={4} maxLength={2000} placeholder="Ej. La alarma suena todo el tiempo y no enciende la pantalla" />
                                </Form.Item>
                                <Form.Item label="Tipo" name="tipoSolicitud">
                                    <Select size="large">
                                        {(ficha.tiposSolicitud || []).map((t) => (
                                            <Option key={t} value={t}>
                                                {t}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <Form.Item label="Su nombre" name="solicitante" rules={[{ required: true, message: "Escriba su nombre" }]}>
                                    <Input size="large" maxLength={120} placeholder="Nombre y área" />
                                </Form.Item>
                                <Form.Item label="Teléfono" name="telefono">
                                    <Input size="large" type="tel" maxLength={40} placeholder="Para contactarle" />
                                </Form.Item>
                                <Form.Item label="Correo" name="correo">
                                    <Input size="large" type="email" maxLength={120} placeholder="Opcional: le avisamos del avance de su reporte" />
                                </Form.Item>
                                <Form.Item label="Fotos (hasta 3)">
                                    <Upload
                                        listType="picture-card"
                                        accept="image/*"
                                        capture="environment"
                                        multiple
                                        fileList={this.state.fotos}
                                        beforeUpload={() => false}
                                        onChange={({ fileList }) => this.setState({ fotos: fileList.slice(0, 3) })}
                                    >
                                        {this.state.fotos.length < 3 && (
                                            <div>
                                                <CameraOutlined style={{ fontSize: 20 }} />
                                                <div style={{ fontSize: 12, marginTop: 4 }}>Foto</div>
                                            </div>
                                        )}
                                    </Upload>
                                </Form.Item>
                                <div style={{ display: "flex", gap: 8 }}>
                                    <Button size="large" onClick={() => this.setState({ reportando: false })} style={{ flex: 1 }}>
                                        Cancelar
                                    </Button>
                                    <Button size="large" type="primary" htmlType="submit" loading={this.state.enviando} style={{ flex: 2 }}>
                                        Enviar reporte
                                    </Button>
                                </div>
                            </Form>
                        </div>
                    )}
                    {(ficha.empresa.telefono || ficha.empresa.correo) && (
                        <div style={{ textAlign: "center", fontSize: 12, color: "#7a8794", marginTop: 14 }}>
                            ¿Urgente? {ficha.empresa.telefono && <a href={`tel:${ficha.empresa.telefono}`}>{ficha.empresa.telefono}</a>}
                            {ficha.empresa.telefono && ficha.empresa.correo ? " · " : ""}
                            {ficha.empresa.correo && <a href={`mailto:${ficha.empresa.correo}`}>{ficha.empresa.correo}</a>}
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default ActivoPublico;
